<template>
    <div>
        <div v-if="orgInvitations.length && !fetchingOrgInvitations">
            <div class="d-flex flex-wrap align-center">
                <div v-if="isOrgManager">
                    <OrganizationInvite />
                </div>
                <v-spacer />
                <v-text-field
                    v-model="invitationEmailSearch"
                    autofocus
                    placeholder="Search for an invitation..."
                    prepend-inner-icon="mdi-magnify "
                    solo
                    flat
                    dense
                    hide-details
                    clearable>
                    <template v-slot:append>
                        <v-menu offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                    <v-icon left>mdi-tune-variant</v-icon>
                                    <span>Filters</span>
                                </v-btn>
                            </template>
                            <v-list nav dense class="d-flex align-start">
                                <div class="filter-section">
                                    <v-subheader>STATUS</v-subheader>
                                    <div v-for="invitationType in availableInvitationTypes" :key="invitationType">
                                        <v-sheet rounded class="py-2 px-4 my-1">
                                            <v-switch v-model="invitationsSwitchData[invitationType]" inset dense hide-details class="pa-0 ma-0">
                                                <template v-slot:label>
                                                    <div class="d-flex align-center">
                                                        <v-icon left small :color="getMailIconColor(invitationType)">mdi-email-outline</v-icon>
                                                        <span>{{ capitalize(invitationAndRequestStatusTypes[invitationType]) }}</span>
                                                    </div>
                                                </template>
                                            </v-switch>
                                        </v-sheet>
                                    </div>
                                </div>
                                <v-divider vertical class="mx-2 align-self-stretch" />
                                <div class="filter-section">
                                    <v-subheader>ROLE</v-subheader>
                                    <div v-for="role in availableRoles" :key="role">
                                        <v-sheet rounded class="py-2 px-4 my-1">
                                            <v-switch v-model="roleFilters[role]" inset dense hide-details class="pa-0 ma-0">
                                                <template v-slot:label>
                                                    <div class="d-flex align-center">
                                                        <v-icon left small>{{ organizationRole(role).icon }}</v-icon>
                                                        <span>{{ capitalize(organizationRole(role).label) }}</span>
                                                    </div>
                                                </template>
                                            </v-switch>
                                        </v-sheet>
                                    </div>
                                </div>
                            </v-list>
                        </v-menu>
                    </template>
                </v-text-field>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn class="ml-2" v-on="on" @click="refreshOrgInvitations" text icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>refresh</span>
                </v-tooltip>
            </div>
            <v-data-table
                :headers="invitationHeaders"
                :items="filteredInvitationsList"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                item-key="invid"
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:[`item.invitation`]="{ item }">
                    <div class="d-flex flex-column py-2">
                        <div>{{ item.requestee_email || '-' }}</div>
                    </div>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <v-chip outlined label small>
                        <v-icon left small :color="getMailIconColor(getInvitationStatus(item))">mdi-email-outline</v-icon>
                        {{ capitalize(getInvitationStatus(item)) }}
                    </v-chip>
                </template>
                <template v-slot:[`item.sent_on`]="{ item }">
                    <div class="py-2 text-no-wrap">
                        <span v-if="item.notification_timestamp">{{ item.notification_timestamp | dateTimeToHuman }}</span>
                        <span v-else>-</span>
                    </div>
                </template>
                <template v-slot:[`item.accepted_on`]="{ item }">
                    <div class="py-2 text-no-wrap">
                        <span v-if="getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.ACCEPTED)">
                            {{ getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.ACCEPTED) | dateTimeToHuman }}
                        </span>
                        <span v-else>-</span>
                    </div>
                </template>
                <template v-slot:[`item.rejected_on`]="{ item }">
                    <div class="py-2 text-no-wrap">
                        <span v-if="getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.REJECTED)">
                            {{ getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.REJECTED) | dateTimeToHuman }}
                        </span>
                        <span v-else>-</span>
                    </div>
                </template>
                <template v-slot:[`item.revoked_on`]="{ item }">
                    <div class="py-2 text-no-wrap">
                        <span v-if="item.revocation_timestamp">{{ item.revocation_timestamp | dateTimeToHuman }}</span>
                        <span v-else>-</span>
                    </div>
                </template>
                <template v-slot:[`item.roles`]="{ item }">
                    <div class="d-flex flex-column py-2">
                        <div v-for="(role, index) in item.roles" :key="index" class="d-flex flex-column">
                            <v-chip v-if="role.role_type === invitationAndRequestRoleTypes.ORG_ROLE" outlined small label class="mb-1 align-self-start">
                                <v-icon left small>{{ organizationRole(role.Name).icon }}</v-icon>
                                <span>{{ capitalize(organizationRole(role.Name).label) }}</span>
                            </v-chip>
                        </div>
                    </div>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu
                        left
                        offset-x
                        nudge-top="10"
                        v-if="
                            isOrgManager &&
                            (getInvitationStatus(item) === invitationAndRequestStatusTypes.PENDING ||
                                getInvitationStatus(item) === invitationAndRequestStatusTypes.CREATED)
                        ">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <InvitationActionsDialog
                                activatorType="listItem"
                                :successFetchAction="
                                    () => {
                                        $store.dispatch('orgStore/fetchOrgInvitations', $route.params.oid)
                                    }
                                "
                                :invitationInfo="item"
                                :actionType="actionTypes.RESEND_INVITATION"></InvitationActionsDialog>
                            <copy-to-clipboard :textToCopy="item.link" buttonName="Copy invitation link" type="listItem"></copy-to-clipboard>
                            <v-divider class="my-2"></v-divider>
                            <InvitationActionsDialog
                                activatorType="listItem"
                                :successFetchAction="
                                    () => {
                                        $store.dispatch('orgStore/fetchOrgInvitations', $route.params.oid)
                                    }
                                "
                                :invitationInfo="item"
                                :actionType="actionTypes.REVOKE_INVITATION"></InvitationActionsDialog>
                        </v-list>
                    </v-menu>
                </template>
                <template v-slot:no-data>
                    <span>No invitations found. Consider inviting new users via the "+ user" button above.</span>
                </template>
            </v-data-table>
        </div>
        <div v-else-if="!orgInvitations.length && !fetchingOrgInvitations">
            <v-banner single-line>
                <v-avatar slot="icon" color="white" size="60">
                    <v-icon x-large color="info">mdi-information-outline</v-icon>
                </v-avatar>
                <div class="d-flex flex-column">
                    <span class="font-weight-bold secondary--text">No invitations found</span>
                    <p>
                        Currently this organization has no invitation history. Consider inviting new users via the
                        <span class="font-weight-bold secondary--text">+ user</span>
                        button above.
                    </p>
                </div>
            </v-banner>
        </div>
        <div v-else-if="fetchingOrgInvitations">
            <div
                v-for="item in [
                    { id: 'c1', opacity: 1 },
                    { id: 'c2', opacity: 0.75 },
                    { id: 'c3', opacity: 0.5 }
                ]"
                :key="item.id"
                class="my-6">
                <div :style="{ opacity: item.opacity }">
                    <v-skeleton-loader type="list-item-avatar-two-line" class="mx-auto" />
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { invitationsAndRequests } from '@/mixins/invitationsAndRequests'
import { sortArray, capitalize, organizationRole } from '@/utils'
import OrganizationInvite from '@/modules/home/components/OrganizationInvite.vue'
const CopyToClipboard = () => import('@/components/CopyToClipboard')
const InvitationActionsDialog = () => import('@/modules/invitations/components/InvitationsActionsDialog')

const createDefaultInvitationsSwitchData = () => ({
    ACCEPTED: true,
    PENDING: true,
    REJECTED: true,
    REVOKED: true,
    EXPIRED: true,
    BROKEN: true,
    CREATED: true
})

const createDefaultRoleFilters = () => ({
    MANAGER: true,
    FACULTY: true,
    MEMBER: true
})

export default {
    name: 'OrganizationUserInvitations',
    mixins: [enumsData, invitationsAndRequests],
    components: {
        CopyToClipboard,
        InvitationActionsDialog,
        OrganizationInvite
    },
    methods: {
        capitalize,
        organizationRole,
        getInvitationResponseTimestamp(invitation, responseType) {
            const responses = invitation?.invitation_responses || []
            const response = responses.find(item => item.response === responseType)
            return response?.timestamp || null
        },
        organizationRoles(invitation) {
            return (invitation.roles || []).filter(role => role.role_type === this.invitationAndRequestRoleTypes.ORG_ROLE)
        },
        resetInvitationFilters() {
            this.invitationsSwitchData = createDefaultInvitationsSwitchData()
            this.roleFilters = createDefaultRoleFilters()
        },
        refreshOrgInvitations() {
            this.resetInvitationFilters()
            this.$store.dispatch('orgStore/fetchOrgInvitations', this.$route.params.oid)
        }
    },
    data() {
        return {
            search: '',
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            invitationsSwitchData: createDefaultInvitationsSwitchData(),
            roleFilters: createDefaultRoleFilters(),
            invitationEmailSearch: ''
        }
    },
    computed: {
        ...mapState('orgStore', ['orgInvitations', 'fetchingOrgInvitations']),
        ...mapGetters('orgStore', ['isOrgManager']),
        invitationHeaders() {
            const baseHeaders = [
                { text: 'Invitation', value: 'invitation', sortable: false },
                { text: 'Status', value: 'status', sortable: false, width: '140px' },
                { text: 'Role', value: 'roles', sortable: false },
                { text: 'Sent on', value: 'sent_on', sortable: false, width: '180px' },
                { text: 'Accepted on', value: 'accepted_on', sortable: false, width: '180px' },
                { text: 'Rejected on', value: 'rejected_on', sortable: false, width: '180px' },
                { text: 'Revoked on', value: 'revoked_on', sortable: false, width: '180px' }
            ]
            if (this.isOrgManager) {
                baseHeaders.push({ text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '100px' })
            }
            return baseHeaders
        },
        availableInvitationTypes() {
            const invitationTypes = []
            if (this.orgInvitations && this.orgInvitations.length) {
                this.orgInvitations.forEach(invitation => {
                    const invitationType = this.getInvitationStatus(invitation)
                    if (!invitationTypes.includes(invitationType)) {
                        invitationTypes.push(invitationType)
                    }
                })
                return invitationTypes
            }
            return []
        },
        displayedInvitationTypes() {
            const filteredInvitations = Object.values(this.invitationAndRequestStatusTypes).filter(
                value => this.availableInvitationTypes.includes(value) && this.invitationsSwitchData[value]
            )
            return filteredInvitations
        },
        availableRoles() {
            return [...new Set(this.orgInvitations.flatMap(invitation => this.organizationRoles(invitation).map(role => role.Name)))].filter(Boolean)
        },
        filteredInvitationsList() {
            const term = this.invitationEmailSearch?.trim().toLowerCase()
            const invitations = this.orgInvitations.filter(invitation => {
                const matchesStatus = this.displayedInvitationTypes.includes(this.getInvitationStatus(invitation))
                const matchesRole = this.organizationRoles(invitation).some(role => this.roleFilters[role.Name])
                const matchesSearch = !term || invitation.requestee_email?.toLowerCase().includes(term)
                return matchesStatus && matchesRole && matchesSearch
            })
            return sortArray(invitations, 'invid', 'descending', false)
        }
    }
}
</script>

<style scoped>
.filter-section {
    min-width: 190px;
}
</style>
